import { createContext, type ReactNode, useContext } from "react";

/**
 * The URL search keys the filter system reads and writes: the free-text
 * `search`, the `filters` object, the chip order `filtersOrder` and the
 * canonical `sort` string.
 */
export type FilterUrlKeys = {
  search: string;
  filters: string;
  filtersOrder: string;
  sort: string;
};

export const DEFAULT_FILTER_URL_KEYS = {
  search: "search",
  filters: "filters",
  filtersOrder: "filtersOrder",
  sort: "sort",
} as const satisfies FilterUrlKeys;

const FilterUrlKeysContext = createContext<FilterUrlKeys>(
  DEFAULT_FILTER_URL_KEYS,
);

/**
 * Moves every filter control beneath it onto other URL keys, for a screen that
 * shows two filtered lists at once and must keep their states apart (the order
 * Files tab beside the aside's place Files tab). The route's `validateSearch`
 * must declare the keys. Without a provider the default keys apply.
 *
 * `useFilters<"/route/id">()`'s typed `filters` describes the route's DEFAULT
 * `filters` key — under a provider, read it bare.
 */
export function FilterUrlKeysProvider({
  keys,
  children,
}: {
  keys: FilterUrlKeys;
  children: ReactNode;
}) {
  return (
    <FilterUrlKeysContext.Provider value={keys}>
      {children}
    </FilterUrlKeysContext.Provider>
  );
}

export function useFilterUrlKeys(): FilterUrlKeys {
  return useContext(FilterUrlKeysContext);
}
